/**
 * CustomGraphics — porte do edvid (assets/shortform/src/CustomGraphics.tsx): as duas peças
 * dirigidas por dados — SplitScreen (`splitInserts`, estilos "Tela dividida" 1 e 2) e CutFlashes
 * (`transitions`) — e, no lugar dos exemplos sob medida do edvid (TimelineGraphic, ScriptGraphic,
 * ShapesGraphic, escritos à mão por vídeo), a BIBLIOTECA de animações parametrizadas
 * (`graphics`, ver ./graphics): a IA escolhe template + props + tempo, o usuário edita na aba Final.
 *
 * Mudança de porte: staticFile('cut.mp4') → props.videoSrc; staticFile(src) → src (URL absoluta).
 * Constantes, clocks e VIDEO_LAG idênticos.
 */
import React, { useMemo } from 'react';
import { AbsoluteFill, Sequence, Img, Video, OffthreadVideo, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import { Sfx, clamp, useReels } from './context';
import { useFontsReady } from './fonts';
import type { EditData, MotionGraphic } from './types';
import { splitImagesAtFrame } from './splitLayout';
import { GRAPHIC_COMPONENTS, GRAPHIC_SPECS, normalizeGraphicHex, validateGraphic, ZONE_MIDDLE_Y, ZONE_TOP_Y } from './graphics';
import type { GraphicLike } from './graphics';
import { EASE_OUT, EASE_SOFT_BACK, GraphicClock, prog } from './graphics/common';

type SplitInsert = NonNullable<EditData['splitInserts']>[number];
type CutFlash = NonNullable<EditData['transitions']>[number];

// Two variants of one idea — both PIN THE FACE to a fixed region and give the
// rest of the frame to the image:
//   'top'    "Tela dividida"   — art on top, head raised underneath
//   'bottom' "Tela dividida 2" — head held high, art underneath
// `focusY` is a SOURCE y that lands at the top of the video window, so a
// point y_src renders at (y_src - focusY) * zoom.
const LAYOUT = {
  top: { zoom: 1.25, focusY: 400 },
  bottom: { zoom: 1.0, focusY: 225 },
} as const;

export const CustomGraphics: React.FC = () => {
  const { editData } = useReels();
  const splits = editData.splitInserts ?? [];
  const flashes = editData.transitions ?? [];
  const graphics = editData.graphics ?? [];
  return (
    <>
      {splits.length ? <SplitScreen items={splits} /> : null}
      {graphics.length ? <MotionGraphics items={graphics} /> : null}
      {flashes.length ? <CutFlashes items={flashes} /> : null}
    </>
  );
};

// ============ MOTION GRAPHICS (biblioteca) ====================================
// Cada gráfico numa <Sequence> própria (relógio local = 0 no início da janela). Entrada
// 8 quadros (blur + subida + back easing, whoosh ~0.09), saída 7 quadros. Tempo em
// SEGUNDOS da fala, não de corte — não segue VIDEO_LAG.
// Regra da capa: um gráfico que começa no quadro 0 já aparece assentado, sem animar.
const G_ENTER = 8;
const G_EXIT = 7;

const MotionGraphics: React.FC<{ items: MotionGraphic[] }> = ({ items }) => {
  const { editData, assetBase } = useReels();
  const { fps } = useVideoConfig();
  // measureText (auto-ajuste dos textos) só depois das fontes: o cache guardaria a largura da substituta
  const fontsReady = useFontsReady(assetBase);
  const valid = useMemo(
    () =>
      items
        .map((g, i) => validateGraphic(g, { durationSec: editData.durationSec, fallbackId: `g${i}` }).graphic)
        .filter((g): g is GraphicLike => Boolean(g)),
    [items, editData.durationSec],
  );
  if (!fontsReady || !valid.length) return null;
  const baseAccent = normalizeGraphicHex(editData.captions?.accent) || normalizeGraphicHex(editData.hook?.accent) || '#ff5200';
  return (
    <>
      {valid.map((g, i) => {
        const from = Math.max(0, Math.round(g.start * fps));
        const dur = Math.max(2, Math.round(g.end * fps) - from);
        const accent = normalizeGraphicHex(g.props.accent) || baseAccent;
        return (
          <Sequence key={`${g.id}-${i}`} from={from} durationInFrames={dur} layout="none">
            <GraphicShell g={g} totalFrames={dur} settled={from === 0} accent={accent} />
          </Sequence>
        );
      })}
    </>
  );
};

const GraphicShell: React.FC<{ g: GraphicLike; totalFrames: number; settled: boolean; accent: string }> = ({ g, totalFrames, settled, accent }) => {
  const f = useCurrentFrame();
  const Comp = GRAPHIC_COMPONENTS[g.template];
  const spec = GRAPHIC_SPECS[g.template];
  // janelas curtas: entrada/saída encolhem juntas para caber
  const k = Math.min(1, totalFrames / (G_ENTER + G_EXIT + 4));
  const enterLen = Math.max(2, Math.round(G_ENTER * k));
  const exitLen = Math.max(2, Math.round(G_EXIT * k));
  const inOp = settled ? 1 : prog(f, 0, enterLen, EASE_OUT);
  const inPop = settled ? 1 : prog(f, 0, enterLen + 2, EASE_SOFT_BACK);
  const out = prog(f, totalFrames - exitLen, totalFrames, EASE_OUT);
  const offset = settled ? Math.max(0, Math.min(90, totalFrames - exitLen - 1)) : 0;

  const motion: React.CSSProperties = spec.selfPositioned
    ? { opacity: inOp * (1 - out), filter: `blur(${(out * 8).toFixed(2)}px)` }
    : {
        opacity: inOp * (1 - out),
        translate: `0px ${((1 - inPop) * 34 - out * 18).toFixed(1)}px`,
        scale: String(interpolate(inPop, [0, 1], [0.92, 1]) - out * 0.03),
        filter: `blur(${((1 - inOp) * 10 + out * 8).toFixed(2)}px)`,
      };
  const body = (
    <GraphicClock offset={offset} silent={settled}>
      <Comp props={g.props} totalFrames={totalFrames} accent={accent} />
    </GraphicClock>
  );

  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      {!settled ? <Sfx src="whoosh.mp3" volume={0.09} /> : null}
      {spec.selfPositioned ? (
        <AbsoluteFill style={motion}>{body}</AbsoluteFill>
      ) : g.zone === 'middle' ? (
        <div style={{ position: 'absolute', left: 0, right: 0, top: ZONE_MIDDLE_Y, height: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={motion}>{body}</div>
        </div>
      ) : (
        <div style={{ position: 'absolute', left: 0, right: 0, top: ZONE_TOP_Y, display: 'flex', justifyContent: 'center' }}>
          <div style={motion}>{body}</div>
        </div>
      )}
    </AbsoluteFill>
  );
};

// OffthreadVideo draws the source frame at or before frame/fps; on an exact frame
// boundary that lands one frame LATE, so the decoded picture changes one
// composition frame after the index says it should. Delay the layout by the same
// frame or the split visibly precedes the cut.
// Padrão MEDIDO no Remotion 4.0.482: 0 (o edvid assumia 1). Por projeto via editData.videoLag.
export const VIDEO_LAG = 0;
export function useVideoLag(): number {
  const lag = useReels().editData.videoLag;
  return Number.isFinite(lag) ? Math.max(0, Math.round(lag as number)) : VIDEO_LAG;
}

// ============ CUT FLASH =======================================================
// Starts BEFORE the cut and peaks on it. `at` is the cut time exactly as
// segments.json states it — VIDEO_LAG lines it up with the frame the picture
// actually changes on, same as the split windows.
const FLASH_LEAD = 2; // frames before the cut
const FLASH_LEN = 7; // total, ~230ms at 30fps

const CutFlashes: React.FC<{ items: CutFlash[] }> = ({ items }) => {
  const frame = useCurrentFrame();
  const { fps, width } = useVideoConfig();
  const lag = useVideoLag();

  const active = items.find((it) => {
    const c = Math.round(it.at * fps) + lag;
    return frame >= c - FLASH_LEAD && frame < c - FLASH_LEAD + FLASH_LEN;
  });
  if (!active) return null;

  const c = Math.round(active.at * fps) + lag;
  const k = active.intensity ?? 1;
  const p = (frame - (c - FLASH_LEAD)) / (FLASH_LEN - 1); // 0..1 pela janela

  // beam sweeps left→right, brightest as it crosses centre
  const x = interpolate(p, [0, 1], [-1.35 * width, 1.35 * width]);
  const beam = interpolate(p, [0, 0.35, 1], [0, 1 * k, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  // the bloom is short and lands ON the cut, not spread across the window
  const bloom = interpolate(frame, [c - 1, c, c + 2], [0, 0.5 * k, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      <AbsoluteFill style={{ backgroundColor: '#fff', opacity: bloom, mixBlendMode: 'screen' }} />
      <AbsoluteFill style={{ overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            top: '-30%',
            left: 0,
            width: width * 0.46,
            height: '160%',
            transform: `translateX(${x.toFixed(1)}px) rotate(-18deg)`,
            background:
              'linear-gradient(90deg,rgba(255,255,255,0) 0%,rgba(255,255,255,0.95) 50%,rgba(255,255,255,0) 100%)',
            opacity: beam,
            mixBlendMode: 'screen',
            filter: 'blur(16px)',
          }}
        />
      </AbsoluteFill>
      <Sequence from={c} durationInFrames={10} layout="none">
        <Sfx src={active.sfx ?? 'cut-click.mp3'} volume={active.volume ?? 0.9} />
      </Sequence>
    </AbsoluteFill>
  );
};

// ============ SPLIT SCREEN ("tela dividida") ==================================
// Each window gets a LOCAL clock so video inserts start at their own frame zero.
// The talking head still needs the GLOBAL composition frame, so its trimBefore is
// offset by the same window start.
const SplitFrame: React.FC<{
  images: SplitInsert[];
  bandH: number;
  fit: 'cover' | 'contain';
  progress: number;
  layout: 'top' | 'bottom';
  baseStartFrame: number;
}> = ({ images, bandH, fit, progress, layout, baseStartFrame }) => {
  const { videoSrc } = useReels();
  // slow Ken-Burns so the band is not a dead still
  const artScale = 1 + 0.03 * progress;
  const { zoom, focusY } = LAYOUT[layout];
  const videoH = 1920 - bandH;
  const bandTop = layout === 'top' ? 0 : videoH;
  const videoTop = layout === 'top' ? bandH : 0;

  return (
    <AbsoluteFill style={{ backgroundColor: '#0a0a0c' }}>
      <div style={{ position: 'absolute', left: 0, top: videoTop, width: 1080, height: videoH, overflow: 'hidden' }}>
        <OffthreadVideo
          src={videoSrc}
          muted
          trimBefore={baseStartFrame}
          style={{
            position: 'absolute',
            width: 1080 * zoom,
            height: 1920 * zoom,
            left: -(1080 * (zoom - 1)) / 2,
            top: -focusY * zoom,
          }}
        />
      </div>

      <div style={{ position: 'absolute', left: 0, top: bandTop, width: 1080, height: bandH, overflow: 'hidden' }}>
        {images.map((image, i) => (
          <div key={`${image.src}-${i}`} style={{ position: 'absolute', left: `${i * 100 / images.length}%`, top: 0, width: `${100 / images.length}%`, height: '100%', overflow: 'hidden', borderLeft: i ? '4px solid #0a0a0c' : undefined, boxSizing: 'border-box' }}>
            {/\.(mp4|mov|webm)(\?|#|$)/i.test(image.src) ? (
              <Video src={image.src} muted loop style={{ width: '100%', height: '100%', objectFit: image.fit ?? fit, scale: String(artScale) }} />
            ) : (
              <Img src={image.src} style={{ width: '100%', height: '100%', objectFit: image.fit ?? fit, scale: String(artScale) }} />
            )}
          </div>
        ))}
        {/* Soft falloff into the seam — 'top' ONLY (the caption rides the seam there). */}
        {layout === 'top' ? (
          <div
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: '100%',
              height: 110,
              background: 'linear-gradient(180deg,rgba(10,10,12,0),rgba(10,10,12,0.75))',
            }}
          />
        ) : null}
      </div>
    </AbsoluteFill>
  );
};

export const SplitScreen: React.FC<{ items: SplitInsert[] }> = ({ items }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const lag = useVideoLag();
  const images = splitImagesAtFrame(items, frame, fps, lag);
  const active = images[0];
  if (!active) return null;
  const a = Math.round(active.start * fps) + lag;
  const b = Math.round(active.end * fps) + lag;
  return (
    <Sequence from={a} durationInFrames={Math.max(1, b - a)}>
      <SplitFrame
        images={images}
        bandH={active.bandH ?? 750}
        fit={active.fit ?? 'cover'}
        layout={active.layout ?? 'top'}
        progress={clamp((frame - a) / Math.max(1, b - a), 0, 1)}
        baseStartFrame={a}
      />
    </Sequence>
  );
};
